import { MaintenanceRecurrence } from '../../../servers-grid/shared'

export interface ApiApplication {
  app_id: string
  archived: boolean
  assess: boolean
  assessPending: boolean
  code: number
  code_shorthand: string
  created: number
  first_seen: number
  importance: number
  importance_description: string
  language: string
  last_reset: null
  last_seen: number
  master: boolean
  missingRequiredFields: string | null
  name: string
  notes: string
  override_url: string | null
  path: string
  policies: string[]
  protect: string | null
  roles: string[]
  short_name: string | null
  size: number
  size_shorthand: string
  status: string
  tags: string[]
  techs: string[]
  total_modules: number
}

export interface Application {
  appId: string
  archived: boolean
  assess: boolean
  assessPending: boolean
  code: number
  codeShorthand: string
  created: number
  firstSeen: number
  importance: number
  importanceDescription: string
  language: string
  lastReset: null
  lastSeen: number
  master: boolean
  missingRequiredFields: string | null
  name: string
  notes: string
  overrideUrl: string | null
  path: string
  policies: string[]
  protect: string | null
  roles: string[]
  shortName: string | null
  size: number
  sizeShorthand: string
  status: string
  tags: string[]
  techs: string[]
  totalModules: number
}

export interface Importance {
  value: string
  name: string
  translationId: string
}

// Fields are limited to those relevant in contrast-ui, Serverless API responds with more
export interface ServerlessApiAccount {
  accountId: string
  active: boolean
  name: string
  orgId: string
  provider: string
  isBlocked?: boolean
}

export interface Rule {
  description: string
  name: string
  title: string
}

export interface LatestUsedCredentials {
  host: string
  id?: string
  name: string
  username: string
  password?: string
}
export interface ApiPriority {
  description: string
  icon_url: string
  id: string
  name: string
}

export interface Priority {
  description: string
  iconUrl: string
  id: string
  name: string
}

export interface Project {
  id: string
  key: string
  name: string
}

export interface Resolution {
  id: string
  name: string
}

export interface ProjectFiledAllowedValue {
  id: string
  name: string
  simplified: string | null
  value?: string
}
export interface ProjectField {
  required: boolean
  name: string
  key: string
  schema: {
    type: string
    system: string | null
    items: string | null
    custom: string | null
    customId: string | null
  }
  autoCompleteUrl: string | null
  allowedValues: ProjectFiledAllowedValue[] | null
  blacklisted: boolean
  fieldKey: string
  arrayTypeWithNoAllowedValues: boolean
  multiUserCustomType: boolean
  empty?: boolean
}

export const ProjectFieldTypes = {
  USER: 'user',
  NUMBER: 'number',
  ARRAY: 'array',
  DATE: 'date',
  STRING: 'string',
  TIME_TRACKING: 'timetracking',
  ANY: 'any',
  OPTION: 'option',
  SELECT: 'select',
  URL: 'url',
  CASCADING_SELECT: 'option-with-child'
}

export interface ProjectUser {
  displayName: string
  active: boolean
  timeZone: string
  locale: string
  accountId: string
  key: string
  emailAddress: string
  name: string
}

export interface JiraLabel {
  name: string
  credentials_id: string
  query: string
}

export interface IssueStatus {
  name: string
  id: string
  statusCategory: {
    key: string
    colorName: string
  }
}

export interface VulnerabilitySubStatus {
  value: 'FP' | 'URL' | 'SC' | 'EC' | 'OT'
  translationId: string
  text: string
}

export interface VulnerabilityStatus {
  value:
    | 'NotAProblem'
    | 'Remediated'
    | 'Fixed'
    | 'Confirmed'
    | 'Suspicious'
    | 'Reported'
    | 'AutoRemediated'
  translationId: string
  label: string
}

export interface ProjectStatus {
  id: number
  name: string
  statuses: IssueStatus[]
}

export interface Epic {
  id?: string
  key: string
  fields: Array<{ name: string; key: string }>
}

export interface IssueType {
  id: number
  name: string
  icon_url: string
}

export type FormProjectField = Partial<{
  _projectField?: ProjectField
  key: string
  type: string
  items: string | null
  value?: any
  empty?: boolean
  id?: string
}>

/**  The payload that the POST API is expecting to update an existing Jira integration */
export interface ApiJiraIntegrationForm {
  name: string
  credentials?: {
    name: string
    username: string
    password: string
    host: string
    credentials_update: boolean
  }
  credentials_id?: string
  applications: string[]
  all_applications: boolean
  exclude_sensitive_info: boolean
  importance: string[]
  app_criterion: boolean
  auto_create_tickets_enabled: boolean
  auto_create_tickets_rules: string[]
  auto_create_tickets_severities: string[]
  auto_create_tickets_all_rules: boolean
  project: string | Project
  epic_id?: string | Epic
  assignee: string
  issue_type: number
  priority_field_available: boolean
  priority_critical: string | null
  priority_high: string | null
  priority_medium: string | null
  priority_low: string | null
  priority_note: string | null
  fields: FormProjectField[]
  serverless_account_ids?: string[]
  serverless?: boolean
  integrations: Array<{
    issueResolution?: string
    issueStatus: string
    traceStatus: string
    traceSubstatus?: string
  }>
  serverless_categories?: string[]
  send_tags: boolean
  maintenance_windows?: NgBugtrackerMaintenanceWindowRequest
}
export interface IntegrationConfig {
  id?: string
  issue_status: IssueStatus | string
  issue_resolution?: Resolution | string
  trace_status: VulnerabilityStatus | string
  trace_substatus?: VulnerabilitySubStatus | string
}

export interface NgBugtrackerMaintenanceWindowApiResource {
  recurrence: MaintenanceRecurrence
  recurrence_days: number[]
  start_time_seconds: number
  duration_seconds: number
}

export interface NgBugtrackerMaintenanceWindowResource {
  recurrence: MaintenanceRecurrence
  recurrenceDays: number[]
  startTimeSeconds: number
  durationSeconds: number
}

export interface NgBugtrackerMaintenanceWindowRequest {
  recurrence: MaintenanceRecurrence
  recurrence_days: number[]
  start_time_seconds: number
  duration_seconds: number
}

/** The payload returned from the Jira Integration GET call in the bugtracker property */
export interface BugtrackerConfig {
  name: string
  username: string
  host: string
  credentials: LatestUsedCredentials
  applications: string[]
  importance: string[]
  assignee: string
  project: string
  fields: FormProjectField[]
  epic_id: string | null
  serverless: boolean
  links: Array<{
    rel: string
    href: string
    hreflang: string | null
    media: string | null
    title: string | null
    type: string | null
    deprecation: string | null
    method: string
  }>
  all_applications: boolean
  app_criterion: boolean
  auto_create_tickets_enabled: boolean
  auto_create_tickets_rules: Rule[]
  auto_create_tickets_severities: string[]
  auto_create_tickets_all_rules: boolean
  bugtracker_id: number
  issue_type: string
  priority_critical: string | null
  priority_high: string | null
  priority_low: string | null
  priority_medium: string | null
  priority_note: string | null
  priority_field_available: boolean | null
  serverless_account_ids: string[]
  serverless_categories: string[]
  integration: boolean
  integrations: IntegrationConfig[]
  exclude_sensitive_info: boolean
  send_tags: boolean
  maintenance_windows?: NgBugtrackerMaintenanceWindowRequest
}
